// src/pages/AdminSettings/AdminSettings.tsx
import React, { useState, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Switch, message, Typography, Space, Spin, Card } from 'antd';

const GET_DISABLE_DOCUMENT_NAME_FALLBACK = gql`
  query DisableUnsortedDocumentsNameFallback {
    disableUnsortedDocumentsNameFallback
  }
`;

const UPDATE_DISABLE_DOCUMENT_NAME_FALLBACK = gql`
  mutation UpdateDisableUnsortedDocumentsNameFallback($disabled: Boolean!) {
    updateDisableUnsortedDocumentsNameFallback(
      disabled: $disabled
    )
  }
`;

const GET_DISABLE_PAYMENT_BATCHES = gql`
  query DisablePaymentBatches {
    disablePaymentBatches
  }
`;

const UPDATE_DISABLE_PAYMENT_BATCHES = gql`
  mutation UpdateDisablePaymentBatches($disabled: Boolean!) {
    updateDisablePaymentBatches(
      disabled: $disabled
    )
  }
`;

const AdminSettings: React.FC = () => {
  // State for the Unsorted Documents Name Fallback toggle
  const { data: documentData, loading: docLoading } = useQuery(GET_DISABLE_DOCUMENT_NAME_FALLBACK, {
    fetchPolicy: 'network-only',
  });
  const [updateDisableUnsortedDocumentsNameFallback] = useMutation(UPDATE_DISABLE_DOCUMENT_NAME_FALLBACK);
  const [unsortedDocumentsNameFallbackDisabled, setUnsortedDocumentsNameFallbackDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (documentData) {
      setUnsortedDocumentsNameFallbackDisabled(documentData.disableUnsortedDocumentsNameFallback);
    }
  }, [documentData]);

  // State for the Automatic Payment Batches toggle
  const { data: paymentData, loading: paymentLoading } = useQuery(GET_DISABLE_PAYMENT_BATCHES, {
    fetchPolicy: 'network-only',
  });
  const [updateDisablePaymentBatches] = useMutation(UPDATE_DISABLE_PAYMENT_BATCHES);
  const [autoBatchesDisabled, setAutoBatchesDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (paymentData) {
      setAutoBatchesDisabled(paymentData.disablePaymentBatches);
    }
  }, [paymentData]);

  // Handle the toggle for Unsorted Documents Name Fallback
  const handleToggleUnsortedDocumentsNameFallback = async (checked: boolean) => {
    try {
      await updateDisableUnsortedDocumentsNameFallback({
        variables: {
          disabled: !checked
        }
      });
      setUnsortedDocumentsNameFallbackDisabled(!checked);
      message.success(`Unsorted Documents Name Fallback ${!checked ? 'disabled' : 'enabled'}`);
    } catch (error) {
      console.error('Failed to update Unsorted Documents Name Fallback setting:', error);
      message.error('Failed to update Unsorted Documents Name Fallback setting');
    }
  };

  // Handle the toggle for Automatic Payment Batches
  const handleToggleAutoBatches = async (checked: boolean) => {
    try {
      await updateDisablePaymentBatches({
        variables: {
          disabled: !checked
        }
      });
      setAutoBatchesDisabled(!checked);
      message.success(`Automatic batches ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to update Automatic Batches setting:', error);
      message.error('Failed to update Automatic Batches setting');
    }
  };

  return (
    <div style={{ padding: 20 }}>
      {docLoading || paymentLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <Spin size="large" tip="Loading..." />
        </div>
      ) : (
        <>
          {/* Feature Flags Section */}
          <Card title="Feature Flags" style={{ width: '100%' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Unsorted Documents Name Fallback Toggle */}
              <Space align="center">
                <Typography.Text>Sort Failed Documents to Customer:</Typography.Text>
                <Switch
                  checked={!unsortedDocumentsNameFallbackDisabled}
                  onChange={handleToggleUnsortedDocumentsNameFallback}
                  loading={docLoading}
                />
                <Typography.Text type="secondary">
                  {!unsortedDocumentsNameFallbackDisabled ? 'Enabled' : 'Disabled'}
                </Typography.Text>
              </Space>

              {/* Automatic Payment Batches Toggle */}
              <Space align="center">
                <Typography.Text>Automatic Payment Batches:</Typography.Text>
                <Switch
                  checked={!autoBatchesDisabled}
                  onChange={handleToggleAutoBatches}
                  loading={paymentLoading}
                />
                <Typography.Text type="secondary">
                  {!autoBatchesDisabled ? 'Enabled' : 'Disabled'}
                </Typography.Text>
              </Space>
            </Space>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminSettings;
